import {
  CAAS_INDEX_SUFFIX,
  DISABLE_ADS,
  IS_DEV_SITE,
} from "@src/app/constants";
import {
  GA4_REF_CODE_CONTENTBLOCK_B,
  GA4_REF_CODE_CONTENTBLOCK_C,
  GA4_REF_CODE_CONTENTBLOCK_J,
} from "@src/app/utils/ga/ref-code";
import {
  BRIGHTCOVE_CONFIG,
  DEFAULT_CONFIG_GAM,
  LOTAME_CONFIG,
  PREMUTIVE_CONFIG,
} from "@src/pubs/common/config";

import newsletterbg from "../hw/Home/assets/newsletter-bg-pattern.webp";
import {
  ConfigAboutUs,
  ConfigAffiliateLibrary,
  ConfigAffiliatesDeclaration,
  ConfigAffiliateShopping,
  ConfigAffiliatesProduct,
  ConfigAmazonAffiliates,
  ConfigAuthorProfile,
  ConfigBrightcove,
  ConfigCaas,
  ConfigCategory,
  ConfigContentHub,
  ConfigError,
  ConfigExploreMore,
  ConfigGAM,
  ConfigGTM,
  ConfigHome,
  ConfigLatestStory,
  ConfigLotame,
  ConfigNewsletter,
  ConfigPermutive,
  ConfigRss,
  ConfigSearch,
  ConfigSingleStory,
  ConfigSite,
  ConfigSkimlinks,
  ConfigTaboola,
  ConfigTag,
  ConfigTeaser,
  ConfigTrackingCode,
  ConfigVideoLanding,
  ConfigVideoOverlayPortrait,
} from "../types/config";
import HomeContentBlockWOTY from "./Home/HomeContentBlockWOTY";
import { moreIcon } from "./icons/moreIcon";
import logoPath from "./logo.svg?url";

export const configSite: ConfigSite = {
  name: "Her World Singapore",
  address: import.meta.env.VITE_PUBLIC_BASE_URL || "https://www.herworld.com/", // without trailing slash
  tagline: "Career, Money, Wellness, Life & Style",
  description:
    "Her World, Singapore's No. 1 women's magazine, brings you the latest news in career, money, lifestyle, fashion and beauty trends, celebrity news and in-depth women features.",
  titleSeparator: "—",
  logoPath,
  sectionLevel3: true,
  twitterSite: "@herworldsg",
  pageShare: {
    size: 35,
    media: [
      {
        name: "Facebook",
      },
      {
        name: "Whatsapp",
      },
      {
        name: "Email",
      },
      {
        name: "LinkedIn",
      },
      {
        name: "X",
      },
    ],
  },
  aboutUs: {
    headlineBelow: true,
  },
  meta: [
    { name: "msapplication-TileColor", content: "#000000" },
    { name: "theme-color", content: "#ffffff" },
  ],
  links: [
    {
      rel: "icon",
      type: "image/png",
      sizes: "32x32",
      href: "/favicon-32x32.png",
    },
    {
      rel: "icon",
      type: "image/png",
      sizes: "16x16",
      href: "/favicon-16x16.png",
    },
    { rel: "mask-icon", href: "/safari-pinned-tab.svg", color: "#b30010" },
  ],
};

export { configHeader } from "./config/configHeader";

export const configCaas: ConfigCaas = {
  index: `herworld${CAAS_INDEX_SUFFIX}`,
  tagIdPrefix: "tag:herworld-keywords@sph.com.sg,2023:",
  cuePublicationName: "herworld",
};

export const configGTM: ConfigGTM = {
  container: "GTM-52VGJMP", //HW
  siteVerification: "_cGEdj2ZMeQhb_OTIAKvlCGbSxaBwct6M6XrDxywcXM",
};

export const configGAM: ConfigGAM = {
  ...DEFAULT_CONFIG_GAM,
  account: "/5908/hw",
  debounce: true,
  debounceDelay: 300,
};

export const configLotame: ConfigLotame = {
  ...LOTAME_CONFIG,
  active: !DISABLE_ADS,
};

export const configPermutive: ConfigPermutive = {
  ...PREMUTIVE_CONFIG,
  active: !DISABLE_ADS,
};

export const configSkimlinks: ConfigSkimlinks = {
  active: !DISABLE_ADS,
  skimlinkId: "157725X1627054",
  geniuslinkId: "319904",
};

export const configBrightcove: ConfigBrightcove = {
  ...BRIGHTCOVE_CONFIG,
};

export const configSingleStory: ConfigSingleStory = {
  authorProfile: true,
  caption: {
    prefix: "Credit: ",
  },
  byline: { defaultCredit: "Her World Team" },
  configShareArticle: {
    label: "Share this article",
    divider: {
      top: false,
    },
  },
  featureStory: {
    skipCoverCaption: true,
    bodyMaxWidth: 830,
  },
  gallery: {
    paginationSeparator: "/",
    galleryItemShareMedia: ["Facebook", "X", "Whatsapp", "Pinterest"],
  },
  displayTags: {
    displayBottomTags: true,
    displayTagHeading: true,
  },
};

export const configContentHub: ConfigContentHub = {
  configAds: {
    showInlineAds: true,
  },
  configMasthead: {
    heading: {
      display: false,
    },
  },
  configBodyContent: {
    withHeading: true,
    display: true,
  },
  configShareButton: {
    display: true,
  },
  configEditorsNote: {
    display: false,
  },
  configCallQoute: {
    display: true,
  },
  configFeaturedSection: {
    display: true,
  },
  storiesPerPage: {
    topStories: 7,
    totalStories: 13,
  },
  configDeals: {
    display: true,
    props: {
      heading: "editor's picks",
      noOfItemsInFrame: 4,
    },
  },
  configTopStories: {
    display: true,
    firstItemAnchorBlock: true,
  },
  socialVideo: {
    display: true,
  },
  contenthubBlockF: {
    cropByIndex: [[0, "wide"]],
  },
  layout: {
    stories: {
      elements: [
        { name: "contentBlockF", limit: 7 },
        { name: "adsMidContent" },
      ],
    },
  },
};

export const configLatestStory: ConfigLatestStory = {
  latestStoryLimit: 20,
  subAnchorLimit: 3,
  landscapeIndexes: [0, 1, 2],
  mainContentBlockDisplay: "columns",
  mainContentBlockMode: "original",
  errorLayoutMode: ["landscape", "square", "landscape"],
  portraitImageWidth: 285,
  landscapeImageWidth: 345,
};

export const configCategory: ConfigCategory = {
  pinnedTeasersLimit: 3,
  latestStoriesLimit: 8,
  adPositioning: 3,
  card: {
    anchorblock: {
      defaultVariant: "anchorblock-stacked-square3026",
      mobileUp: {
        defaultVariant: "anchorblock-list-mobile-up",
      },
    },
    subAnchor: {
      defaultVariant: "card-list-summary-border-original",
      mobileUp: {
        defaultVariant: "card-list-summary-border-landscape-mobile-up",
      },
    },
    latestArticle: {
      defaultVariant: "card-list-original",
      mobileUp: {
        defaultVariant: "card-stacked-original-mobile-up",
      },
    },
  },
};

export const configTag: ConfigTag = {
  topStoriesLimit: 4,
  listedStoriesLimit: 12,
};

export const configAmazonAffiliates: ConfigAmazonAffiliates = {
  partnerTag: IS_DEV_SITE ? "sphmedia-dev-22" : "sphmedia-herworld-22",
};

export const configTaboola: ConfigTaboola = {
  publisherId: "sphmedia-herworld",
};

export const configSearch: ConfigSearch = {};

export const configExploreMore: ConfigExploreMore = {
  heading: "Explore more topics",
};

export const configVideoLanding: ConfigVideoLanding = {
  videoPlaylistFillUpSize: 9,
  videoPath: "/video",
};

export const configVideoOverlayPortrait: ConfigVideoOverlayPortrait = {};

export const configHome: ConfigHome = {
  headingDivider: false,
  isHeadingClickable: true,
  anchorsWithDescription: ["trending", "videoplaylist"],
  viewMore: { icon: moreIcon },
  latestArticle: {
    skipImage: true,
    landscapeIndexes: [0, 3],
    viewMore: {
      text: "View more",
    },
  },
  contentBlockB: {
    cropsByIndex: [
      [0, "landscape"],
      [1, "square"],
      [2, "landscape"],
    ],
    itemCenteredIndex: 1,
  },
  contentBlockF: {
    skipSectionContext: true,
    landscapeIndexes: [0, 1, 2],
    layoutTwoColumn: [1, 2],
    anchorBlock: {
      index: 0,
      mediaScreen: "all-screen",
    },
    landscapeImageWidth: 630,
    twoColumnsLandscapeImageWidth: 300,
  },
  contentBlockD: {
    descriptionInTitle: true,
    squareIndexes: [0, 2],
  },
  contentBlockG: {
    skipSectionContext: true,
    landscapeIndexes: [1, 2],
    landscapeImageWidth: 300,
    portraitImageWidth: 300,
  },
  videoGalleryLandscape: {
    hasPagination: false,
    viewMore: {
      href: "/video",
    },
    strip: {
      icon: true,
      dividerVariant: "primary",
    },
    spotLight: {
      mobileLoopingArrowsBelow: true,
    },
  },
  contentColumnSideScroll: {
    section: "shopping",
  },
  affiliateProduct: {
    description: "as curated by the Her World Style team",
  },
  magazineListing: {
    midDivider: true,
  },
  newsletter: {
    containerImageBg: newsletterbg,
    newsletterTitle: `Your life. Your rules. Your style. We’ve got you covered.`,
    newsletterBody: `Sign up for our newsletter for the latest news from Her World.`,
  },
  trending: {
    limit: 4,
  },
  layoutGrid: {
    elements: [
      {
        name: "anchorBlock",
        dataSource: {
          type: "list",
          key: "anchorblock",
          fallback: "latest",
          limit: 1,
        },
      },
      {
        name: "latestArticle",
        dataSource: {
          type: "list",
          key: "pinned",
          fallback: "latest",
          limit: 4,
        },
      },
      {
        Component: HomeContentBlockWOTY,
        dataSource: {
          type: "tag",
          key: "woty",
          min: 2,
          limit: 2,
        },
      },
      {
        name: "contentBlockD",
        dataSource: {
          type: "list",
          key: "trending",
          fallback: "trending",
          refCode: "trending",
          limit: 4,
        },
      },
      {
        name: "adUnit",
      },
      {
        name: "socialVideoPortrait",
        dataSource: { type: "list", key: "socialvideos" },
      },
      {
        name: "affiliateProduct",
        dataSource: {
          type: "list",
          key: "deals",
        },
      },
      {
        name: "contentBlockF",
        dataSource: {
          type: "section",
          key: "independence_career",
          limit: 3,
          refCode: "block-f",
        },
        alternateBg: true,
      },
      {
        name: "columnSideScroll",
        dataSource: {
          type: "section",
          key: "shopping",
          limit: 10,
          refCode: "column-side-scroll",
        },
      },
      {
        name: "contentBlockG",
        dataSource: {
          type: "section",
          key: "independence_money",
          limit: 4,
          refCode: "block-g",
        },
      },
      {
        name: "videoGalleryLandscape",
        dataSource: {
          type: "list",
          key: "videoplaylist",
          refCode: "videoplaylist",
          fallback: "videoplaylist",
          limit: 4,
        },
      },
      {
        name: "contentBlockB",
        dataSource: {
          type: "tag",
          key: "topic-0",
          limit: 3,
          min: 3,
          refCode: "block-b",
        },
        alternateBg: true,
      },
      { name: "magazineListing" },
      { name: "newsletter" },
    ],
  },
  trendingStories: { publisher: "herworld" },
};

export const configNewsletter: ConfigNewsletter = {
  image: newsletterbg,
  title: `Your life. Your rules. Your style. We’ve got you covered.`,
  body: `Sign up for our newsletter for the latest news from Her World.`,
};

export const configTeaser: ConfigTeaser = {};

export const configTrackingCode: ConfigTrackingCode = {
  contenthub: {
    topstories: {
      contentBlockB: GA4_REF_CODE_CONTENTBLOCK_B,
      contentBlockF: GA4_REF_CODE_CONTENTBLOCK_J,
    },
    featured: GA4_REF_CODE_CONTENTBLOCK_C,
  },
};

export const configAffiliatesDeclaration: ConfigAffiliatesDeclaration = {};

export const configError: ConfigError = {};

export const configAuthorProfile: ConfigAuthorProfile = {
  showReadMore: true,
  hideDivider: true,
  listingLayout: {
    mainContentBlockDisplay: "columns",
    mainContentBlockMode: "original",
  },
};

export const configAboutUs: ConfigAboutUs = {
  profileBlock: {
    heading: "meet the team",
  },
};

export const configAffiliatesProduct: ConfigAffiliatesProduct = {};

export const configAffiliateShopping: ConfigAffiliateShopping = {
  withDivider: false,
  layoutGrid: {
    elements: [
      {
        name: "shopTheEdit",
        dataSource: {
          types: ["list"],
          key: "products-7-grid",
          limit: 7,
          config: {
            cta: {
              name: "SHOP MORE IN STORY",
            },
            anchorBlock: {
              cardLayout: {
                defaultVariant: "anchorblock-overlap-summary",
                mobileUp: {
                  defaultVariant: "anchorblock-overlap-summary-mobile-up",
                },
              },
            },
            moreStories: {
              ads: {
                sidebar: true,
              },
              cardLayout: {
                defaultVariant: "card-stacked-landscape",
                mobileUp: {
                  defaultVariant:
                    "card-list-summary-border-landscape-mobile-up",
                },
              },
            },
            productMobileLayout: "list",
          },
        },
      },
      {
        name: "products",
        dataSource: {
          types: ["list"],
          key: "products-6-grid",
          limit: 6,
        },
      },
      {
        name: "shopTheEdit",
        dataSource: {
          types: ["section"],
          key: "stories-4",
          limit: 4,
          config: {
            backgroundColor: "#000",
            anchorBlock: {
              cardLayout: {
                defaultVariant: "anchorblock-overlap-summary",
                mobileUp: {
                  defaultVariant: "anchorblock-overlap-summary-mobile-up",
                },
              },
            },
            moreStories: {
              ads: {
                sidebar: true,
              },
              cardLayout: {
                defaultVariant: "card-stacked-landscape",
                mobileUp: {
                  defaultVariant:
                    "card-list-summary-border-landscape-mobile-up",
                },
              },
            },
          },
        },
      },
      {
        name: "shopTheEdit",
        dataSource: {
          types: ["list"],
          key: "products-4-grid",
          limit: 4,
          config: {
            cta: {
              name: "SHOP MORE IN STORY",
            },
            anchorBlock: {
              cardLayout: {
                defaultVariant: "anchorblock-overlap-summary",
                mobileUp: {
                  defaultVariant: "anchorblock-overlap-summary-mobile-up",
                },
              },
            },
            productMobileLayout: "grid",
          },
        },
      },
      {
        name: "shopTheEdit",
        dataSource: {
          types: ["section", "tag"],
          key: "stories-3",
          limit: 3,
          config: {
            backgroundColor: "#000",
            moreStories: {
              isLandscape: true,
              cardLayout: {
                defaultVariant: "card-stacked-landscape",
                mobileUp: {
                  defaultVariant: "card-stacked-landscape-mobile-up",
                },
              },
              layout: {
                mobileUp: "stacked",
              },
            },
          },
        },
      },
      {
        name: "shopTheEdit",
        dataSource: {
          types: ["section", "tag"],
          key: "stories-12",
          limit: 12,
          config: {
            cta: {
              name: "VIEW ALL SHOP",
            },
            moreStories: {
              isLandscape: true,
              hasCta: true,
              ads: {
                sidebar: false,
                leaderboard: true,
              },
              cardLayout: {
                defaultVariant: "card-stacked-landscape",
                mobileUp: {
                  defaultVariant: "card-stacked-landscape-mobile-up",
                },
              },
              layout: {
                mobileUp: "stacked",
              },
            },
          },
        },
      },
    ],
  },
  viewMore: { icon: moreIcon },
};
export const configRss: ConfigRss = [];

export const configAffiliateLibrary: ConfigAffiliateLibrary = {
  enable: true,
  breakpoints: [
    {
      containerMinWidth: 1000,
      visibleItems: 5,
    },
    {
      containerMinWidth: 656,
      visibleItems: 4,
    },
    {
      containerMinWidth: 600,
      visibleItems: 3,
    },
    {
      containerMinWidth: 420,
      visibleItems: 2,
    },
  ],
};
